import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  NavigateBefore as NavigateBeforeIcon,
  NavigateNext as NavigateNextIcon,
  RadioButtonUnchecked as RadioButtonUncheckedIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api, { handleCatch } from '../api';
import colors from '../common/Colors';
import MobileHeader from '../components/MobileHeader';

const readStoredExam = () => {
  try {
    const storedExam = localStorage.getItem('currentExam');
    return storedExam ? JSON.parse(storedExam) : null;
  } catch (error) {
    console.error('Error reading current exam:', error);
    return null;
  }
};

const getRemainingMinutes = (seconds) => Math.max(0, Math.ceil(seconds / 60));

const formatTime = (seconds) => {
  const safeSeconds = Math.max(0, seconds);
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const remainingSeconds = safeSeconds % 60;
  const twoDigits = (value) => String(value).padStart(2, '0');

  if (hours > 0) {
    return `${hours}:${twoDigits(minutes)}:${twoDigits(remainingSeconds)}`;
  }

  return `${twoDigits(minutes)}:${twoDigits(remainingSeconds)}`;
};

const buildAnswerMap = (answers = []) =>
  answers.reduce((map, item) => {
    const questionId = Number(item.questionId);
    if (questionId) {
      map[questionId] = {
        questionId,
        studentAnswer: Number(item.studentAnswer || 0),
        status: item.status || 'NotVisited',
      };
    }
    return map;
  }, {});

const buildAnswerList = (answerMap) =>
  Object.values(answerMap).map((item) => ({
    questionId: item.questionId,
    studentAnswer: item.studentAnswer || 0,
    status: item.status || 'NotVisited',
  }));

const getQuestionOptions = (question) => [
  { value: 1, label: question?.optionOne || question?.option1 },
  { value: 2, label: question?.optionTwo || question?.option2 },
  { value: 3, label: question?.optionThree || question?.option3 },
  { value: 4, label: question?.optionFour || question?.option4 },
].filter((option) => option.label);

const OnlineExamStartScreen = () => {
  const navigate = useNavigate();
  const [exam] = useState(readStoredExam);
  const [answerMap, setAnswerMap] = useState(() => buildAnswerMap(readStoredExam()?.answers || []));
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [remainingSeconds, setRemainingSeconds] = useState(() => {
    const storedExam = readStoredExam();
    return Math.max(0, Number(storedExam?.leftDuration || 0) * 60);
  });
  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [finishing, setFinishing] = useState(false);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  const examRef = useRef(exam);
  const answerMapRef = useRef(answerMap);
  const remainingSecondsRef = useRef(remainingSeconds);
  const finishedRef = useRef(false);

  const questions = useMemo(() => (Array.isArray(exam?.questions) ? exam.questions : []), [exam]);
  const currentQuestion = questions[currentQuestionIndex];
  const currentAnswer = currentQuestion ? answerMap[currentQuestion.id]?.studentAnswer || 0 : 0;
  const attemptedCount = useMemo(
    () => Object.values(answerMap).filter((answer) => Number(answer.studentAnswer) > 0).length,
    [answerMap]
  );
  const progressValue = questions.length > 0 ? (attemptedCount / questions.length) * 100 : 0;

  useEffect(() => {
    answerMapRef.current = answerMap;
  }, [answerMap]);

  useEffect(() => {
    remainingSecondsRef.current = remainingSeconds;
  }, [remainingSeconds]);

  const persistCurrentExam = useCallback((nextAnswerMap, nextRemainingSeconds) => {
    const currentExam = examRef.current;
    if (!currentExam) return;

    localStorage.setItem(
      'currentExam',
      JSON.stringify({
        ...currentExam,
        leftDuration: getRemainingMinutes(nextRemainingSeconds),
        answers: buildAnswerList(nextAnswerMap),
      })
    );
  }, []);

  const updateDuration = useCallback(async () => {
    const examId = examRef.current?.examId;
    if (!examId) return;

    const token = localStorage.getItem('userToken');
    await api.post(
      `api/school-common/online-exam/update-exam-duration?examId=${examId}&duration=${getRemainingMinutes(
        remainingSecondsRef.current
      )}`,
      {},
      {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      }
    );
  }, []);

  const finishExam = useCallback(
    async (autoSubmit = false) => {
      const examId = examRef.current?.examId;
      if (!examId || finishedRef.current) return;

      finishedRef.current = true;
      setFinishing(true);
      setFinishDialogOpen(false);

      try {
        await updateDuration().catch(() => {});
        const token = localStorage.getItem('userToken');
        await api.post(
          `api/school-common/online-exam/finish-exam?examId=${examId}`,
          {},
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );

        localStorage.removeItem('currentExam');
        setNotification({
          open: true,
          message: autoSubmit ? 'Time is up. Exam submitted successfully.' : 'Exam submitted successfully.',
          severity: 'success',
        });
        window.setTimeout(() => navigate('/exam-results', { replace: true }), 900);
      } catch (error) {
        console.error('Error finishing exam:', error);
        finishedRef.current = false;
        handleCatch(error);
        setNotification({ open: true, message: 'Could not submit exam. Please try again.', severity: 'error' });
      } finally {
        setFinishing(false);
      }
    },
    [navigate, updateDuration]
  );

  useEffect(() => {
    if (!exam?.examId || questions.length === 0) {
      navigate('/online-exams', { replace: true });
    }
  }, [exam, navigate, questions.length]);

  useEffect(() => {
    if (!exam?.examId || finishing) return undefined;

    const timerId = window.setInterval(() => {
      setRemainingSeconds((previousSeconds) => Math.max(0, previousSeconds - 1));
    }, 1000);

    return () => window.clearInterval(timerId);
  }, [exam?.examId, finishing]);

  useEffect(() => {
    if (!exam?.examId) return;
    persistCurrentExam(answerMapRef.current, remainingSeconds);

    if (remainingSeconds === 0 && !finishedRef.current) {
      finishExam(true);
    }
  }, [exam?.examId, finishExam, persistCurrentExam, remainingSeconds]);

  useEffect(() => {
    if (!exam?.examId) return undefined;

    const durationSyncId = window.setInterval(() => {
      updateDuration().catch((error) => {
        console.error('Error updating exam duration:', error);
      });
    }, 60000);

    return () => window.clearInterval(durationSyncId);
  }, [exam?.examId, updateDuration]);

  const saveAnswer = async (optionValue) => {
    if (!currentQuestion || finishing) return;

    const questionId = currentQuestion.id;
    const nextAnswerMap = {
      ...answerMap,
      [questionId]: {
        questionId,
        studentAnswer: optionValue,
        status: 'Attempted',
      },
    };

    setAnswerMap(nextAnswerMap);
    persistCurrentExam(nextAnswerMap, remainingSecondsRef.current);
    setSavingQuestionId(questionId);

    try {
      const token = localStorage.getItem('userToken');
      await api.post(
        `api/school-common/online-exam/update-answer?examId=${exam.examId}&questionId=${questionId}&answer=${optionValue}&status=Attempted`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
    } catch (error) {
      console.error('Error saving answer:', error);
      handleCatch(error);
      setNotification({ open: true, message: 'Could not save this answer. Please try again.', severity: 'error' });
    } finally {
      setSavingQuestionId(null);
    }
  };

  const goToPreviousQuestion = () => {
    setCurrentQuestionIndex((index) => Math.max(0, index - 1));
  };

  const goToNextQuestion = () => {
    setCurrentQuestionIndex((index) => Math.min(questions.length - 1, index + 1));
  };

  if (!exam || !currentQuestion) {
    return (
      <Box sx={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Box>
    );
  }

  const options = getQuestionOptions(currentQuestion);
  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const renderQuestionNavigation = () => (
    <Box
      sx={{
        p: 1.5,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      }}
    >
      <Typography sx={{ fontWeight: 'bold', fontSize: 13, color: '#222', mb: 1 }}>
        Questions
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
          gap: 1,
        }}
      >
        {questions.map((question, index) => {
          const answered = Number(answerMap[question.id]?.studentAnswer || 0) > 0;
          const active = index === currentQuestionIndex;
          return (
            <Button
              key={question.id}
              variant={active || answered ? 'contained' : 'outlined'}
              onClick={() => setCurrentQuestionIndex(index)}
              sx={{
                minWidth: 0,
                p: 0,
                width: 38,
                height: 38,
                aspectRatio: '1 / 1',
                borderRadius: '50%',
                justifySelf: 'center',
                flexShrink: 0,
                bgcolor: active ? colors.BLUE : answered ? colors.GREEN : '#fff',
                color: active || answered ? '#fff' : '#555',
                borderColor: active || answered ? 'transparent' : '#D8DCE4',
                '&:hover': {
                  bgcolor: active ? '#0052A3' : answered ? colors.GREEN : '#F7F8FC',
                },
              }}
            >
              {index + 1}
            </Button>
          );
        })}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', bgcolor: '#F5F7FA' }}>
      <MobileHeader
        title={exam.examName || 'Online Exam'}
        showBack={true}
        onBack={() =>
          setNotification({ open: true, message: 'Please finish the exam before leaving.', severity: 'warning' })
        }
        rightAction={
          <Chip
            icon={<TimerIcon sx={{ color: '#fff !important' }} />}
            label={formatTime(remainingSeconds)}
            sx={{ bgcolor: remainingSeconds <= 300 ? colors.RED : 'rgba(255,255,255,0.18)', color: '#fff' }}
          />
        }
      />

      <Box
        sx={{
          bgcolor: '#fff',
          borderBottom: '1px solid #E0E4EA',
          px: 1.5,
          py: 1,
          zIndex: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
            mb: 0.8,
          }}
        >
          <Typography sx={{ fontWeight: 'bold', fontSize: 14, color: '#222' }}>
            Question {currentQuestionIndex + 1} of {questions.length}
          </Typography>
          <Chip
            icon={<TimerIcon sx={{ color: '#fff !important' }} />}
            label={formatTime(remainingSeconds)}
            sx={{ bgcolor: remainingSeconds <= 300 ? colors.RED : colors.BLUE, color: '#fff', fontWeight: 'bold' }}
          />
          <Typography sx={{ fontSize: 12, color: '#666' }}>
            {attemptedCount}/{questions.length} Attempted
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressValue}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: '#E8EAF6',
            '& .MuiLinearProgress-bar': { bgcolor: colors.BLUE },
          }}
        />
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1, pt: 1, pb: 2 }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 92px', sm: '1fr 150px' },
            gap: 1,
            alignItems: 'start',
          }}
        >
          <Box
            sx={{
              bgcolor: '#fff',
              borderRadius: 2,
              boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
              mx: 0.5,
              overflow: 'hidden',
            }}
          >
            <Box sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Chip label={`${currentQuestion.marks || 0} Marks`} size="small" sx={{ bgcolor: colors.LIGHT_BLUE }} />
                {savingQuestionId === currentQuestion.id && (
                  <Typography sx={{ fontSize: 12, color: colors.BLUE }}>Saving...</Typography>
                )}
              </Box>
              <Typography sx={{ fontWeight: 'bold', fontSize: 16, color: '#222', lineHeight: 1.45 }}>
                {currentQuestion.question}
              </Typography>
            </Box>

            <Divider />

            <Box sx={{ p: 2 }}>
              {options.map((option) => {
                const selected = currentAnswer === option.value;
                return (
                  <Button
                    key={option.value}
                    fullWidth
                    variant="outlined"
                    onClick={() => saveAnswer(option.value)}
                    disabled={finishing}
                    startIcon={selected ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                    sx={{
                      justifyContent: 'flex-start',
                      textAlign: 'left',
                      mb: 1,
                      py: 1.2,
                      px: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                      fontWeight: selected ? 'bold' : 'normal',
                      color: selected ? colors.BLUE : '#333',
                      borderColor: selected ? colors.BLUE : '#D8DCE4',
                      bgcolor: selected ? colors.LIGHT_BLUE : '#fff',
                      '&:hover': {
                        borderColor: colors.BLUE,
                        bgcolor: selected ? colors.LIGHT_BLUE : '#F7F8FC',
                      },
                    }}
                  >
                    {option.label}
                  </Button>
                );
              })}
              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                <Button
                  variant="outlined"
                  startIcon={<NavigateBeforeIcon />}
                  onClick={goToPreviousQuestion}
                  disabled={currentQuestionIndex === 0 || finishing}
                  sx={{ flex: 1, textTransform: 'none' }}
                >
                  Previous
                </Button>
                {isLastQuestion ? (
                  <Button
                    variant="contained"
                    onClick={() => setFinishDialogOpen(true)}
                    disabled={finishing}
                    sx={{ flex: 1, bgcolor: colors.GREEN, textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {finishing ? 'Submitting...' : 'Finish Exam'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    endIcon={<NavigateNextIcon />}
                    onClick={goToNextQuestion}
                    disabled={finishing}
                    sx={{ flex: 1, bgcolor: colors.BLUE, textTransform: 'none' }}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Box>
          </Box>

          <Box sx={{ position: 'sticky', top: 8 }}>
            {renderQuestionNavigation()}
          </Box>
        </Box>
      </Box>

      <Dialog open={finishDialogOpen} onClose={() => !finishing && setFinishDialogOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Finish Exam?</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: '#555' }}>
            You have attempted {attemptedCount} out of {questions.length} questions. Once submitted, you cannot change
            your answers.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinishDialogOpen(false)} disabled={finishing}>
            Continue Exam
          </Button>
          <Button onClick={() => finishExam(false)} variant="contained" disabled={finishing} sx={{ bgcolor: colors.GREEN }}>
            {finishing ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default OnlineExamStartScreen;
